import React, { useEffect, useState } from 'react';
import { Stack, Typography, Box, Card, CardMedia, IconButton } from '@mui/material';

import { request } from '../../../Utils/request';
import { Share as ShareIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { paiseToRupees } from '../../../Utils/currencyConverter';
import { useSelector } from 'react-redux';
import ShareModal from '../../../Components/ShareModal';



/**
 * Displays a simple list of reservations made by the user.
 * Currently it renders all trips that are passed in. If a dedicated
 * reservation status is available from the API you can pre-filter the
 *   array before passing it to this component.
 */
const ReservationsTab: React.FC = () => {
  const [myReservations, setMyReservations] = useState<any[]>([]);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<any>(null);
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  
  useEffect(() => {
    const fetchMyReservations = async () => {
      const res = await request<{ data: any }>('/bookings/me', {
        method: 'GET',
        authorization: true,
      });
      setMyReservations(res?.data);
    }
    if(isLoggedIn)
    fetchMyReservations();
  }, []);

  const handleShare = (reservation: any) => {
    setSelectedReservation(reservation);
    setShareModalOpen(true);
  };

  const handleShareModalClose = () => {
    setShareModalOpen(false);
    setSelectedReservation(null);
  };

  const getShareUrl = (reservation: any) => {
    const tripId = reservation.tripId || reservation.trip?._id || reservation._id;
    if (!tripId) return '';
    const baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://ifinds.in' 
      : window.location.origin.replace('https://', 'http://');
    return `${baseUrl}/trips/${tripId}`;
  };
  if(myReservations.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No Reservations found
        </Typography>
      </Box>
    )
  }
  return (
    <Box sx={{}}>
      <Stack spacing={2} sx={{marginBottom:'100px'}}>
        {  myReservations.map((reservation) => (
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 1,
                mb: 2,
                overflow: 'hidden',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 2,
                },
              }}
              onClick={() => {
                if(reservation.type === 'trip'){
                  navigate(`/trip/bookinginfo`, { 
                    state: { bookingData: reservation } 
                  });
                }else{
                  navigate(`/trips/${reservation.itineraryId}?type=itinerary`);
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                <CardMedia
                  component="img"
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 2,
                    mr: 2,
                    objectFit: 'cover'
                  }}
                  image={reservation.files?.[0]?.fileURL }
                  alt={reservation.title || reservation.name}
                />
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                    {reservation.title || reservation.name || reservation.destination}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {reservation.days || reservation.duration || '3'} Days • ₹{paiseToRupees(reservation.pricePerPerson || reservation.price ).toFixed(2)}/person
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Box
                      onClick={() => {
                        navigate(`/profile/${reservation.user?._id}`);
                      }}
                      sx={{
                        // background: 'linear-gradient(90deg, #FF9800 0%, #FF5722 100%)',
                        background: 'white',
                        // color: '#fff',
                        borderRadius: 2,
                        borderColor: 'black',
                        borderWidth: 1,
                        px: 2,
                        py: 0.5,
                        fontSize: '12px',
                        fontWeight: 500,
                        minWidth: 'fit-content',
                        height: 26,
                      }}
                    >
                      {reservation.user?.username}
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShare(reservation);
                      }}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        width: 36,
                        height: 36,
                        // ml: 'auto',
                      }}
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bgcolor: '#FFE0B2',
                  color: '#8B4513',
                  borderRadius: '0 20px 0 16px',
                  px: 2,
                  py: 0.5,
                  fontSize: '12px',
                  fontWeight: 500,
                  boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                }}
              >
                {reservation.type[0].toUpperCase()+reservation.type.slice(1)}
              </Box>
            </Card>
          ))
        }
      </Stack>

      {/* Share Modal */}
      <ShareModal
        open={shareModalOpen}
        onClose={handleShareModalClose}
        title={selectedReservation?.title || selectedReservation?.name || selectedReservation?.destination || 'Trip'}
        shareUrl={selectedReservation ? getShareUrl(selectedReservation) : ''}
        shareText={selectedReservation?.description || `Check out this trip: ${selectedReservation?.title || selectedReservation?.name || ''}`}
      />
    </Box>
  );
};

export default ReservationsTab;
